/**
 * Customer storefront origin for "View page" / sitemap links in admin.
 *
 * On localhost, always preview the local storefront (port 3000) unless
 * VITE_PUBLIC_SITE_URL is set. Elsewhere:
 * 1. Admin → Settings → Company → Customer site URL
 * 2. VITE_PUBLIC_SITE_URL / VITE_CUSTOMER_SITE_URL
 * 3. http://localhost:3000
 */

let cachedCustomerSiteUrl = '';

/** Call after public system_settings load (or when Company settings are saved). */
export function setPublicCustomerSiteUrl(url) {
  cachedCustomerSiteUrl = String(url || '')
    .trim()
    .replace(/\/$/, '');
}

export function publicSiteOrigin() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      const fromEnv =
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PUBLIC_SITE_URL) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CUSTOMER_SITE_URL) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CUSTOMER_APP_URL) ||
        '';
      const trimmed = String(fromEnv || '').trim().replace(/\/$/, '');
      if (trimmed) {
        try {
          const withScheme = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
          return new URL(withScheme).origin;
        } catch {
          /* fall through to local default */
        }
      }
      return 'http://localhost:3000';
    }
  }

  const fromSettings = cachedCustomerSiteUrl;
  if (fromSettings) {
    try {
      const withScheme = fromSettings.includes('://') ? fromSettings : `https://${fromSettings}`;
      return new URL(withScheme).origin;
    } catch {
      /* fall through */
    }
  }

  const raw =
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PUBLIC_SITE_URL) ||
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CUSTOMER_SITE_URL) ||
    (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CUSTOMER_APP_URL) ||
    '';
  const trimmed = String(raw || '').trim().replace(/\/$/, '');
  if (trimmed) {
    try {
      const withScheme = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
      return new URL(withScheme).origin;
    } catch {
      return trimmed;
    }
  }
  return 'http://localhost:3000';
}

/** Hosts that belong to this product — rewrite onto the current customer origin. */
function isTejasPublicHost(hostname) {
  const host = String(hostname || '').toLowerCase().replace(/\.$/, '');
  if (!host) return false;
  if (host === 'localhost' || host === '127.0.0.1') return true;
  return host === 'tejasfleets.com' || host.endsWith('.tejasfleets.com');
}

/** Absolute frontend URL for a tariff/public path or slug. */
export function publicSitePageUrl(pathOrSlug) {
  const raw = String(pathOrSlug || '').trim();
  if (!raw) return publicSiteOrigin();
  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      if (isTejasPublicHost(parsed.hostname)) {
        return `${publicSiteOrigin()}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return raw;
    }
    return raw;
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${publicSiteOrigin()}${path}`;
}
